import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  output,
  viewChild,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { InteractionStatus, InteractionType } from '@azure/msal-browser';
import { ModalService, IconV2Module } from '@schenck/rotec-components';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { routes } from 'src/utils/constants/routes';
import { LanguageModalComponent } from '../modals/language-modal/language-modal.component';

@Component({
  selector: 'am-app-header',
  templateUrl: './app-header.component.html',
  styleUrls: ['./app-header.component.less'],
  imports: [IconV2Module],
})
export class AppHeaderComponent implements OnInit, OnDestroy {
  private readonly authService = inject(MsalService);
  private readonly broadcastService = inject(MsalBroadcastService);
  private readonly msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private readonly router = inject(Router);
  private readonly modalService = inject(ModalService);

  public readonly username = viewChild<ElementRef>('username');
  public readonly userChange = output<boolean>();

  @HostListener('document:click', ['$event.target']) public onClick(event: Event): void {
    const parent = this.username()?.nativeElement;

    if (!parent?.contains(event)) {
      this.hideUserMenu = true;
    }
  }

  public loginDisplay = false;
  public user: string;
  public hideUserMenu = true;

  private readonly _destroy$ = new Subject<void>();

  public ngOnInit(): void {
    this.authService.handleRedirectObservable().pipe(takeUntil(this._destroy$)).subscribe();

    this.broadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroy$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
      });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public logout(): void {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.authService.logoutPopup({
        postLogoutRedirectUri: '/',
        mainWindowRedirectUri: '/',
      });
    } else {
      this.authService.logoutRedirect({
        postLogoutRedirectUri: '/',
      });
    }
  }

  public toggleUserMenu() {
    this.hideUserMenu = !this.hideUserMenu;
  }

  public openLanguageModal() {
    this.modalService.open(LanguageModalComponent);
  }

  private setLoginDisplay(): void {
    const accounts = this.authService.instance.getAllAccounts();

    this.loginDisplay = accounts.length > 0;
    this.user = accounts[0]?.username;

    if (this.user) {
      // only redirect if we are on the login page
      if (this.router.url.includes(routes.login.absoluteUrl)) {
        this.router.navigate([routes.tenants.absoluteUrl]);
      }

      this.userChange.emit(this.loginDisplay);
    }
  }
}
